import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Link,
  ListItemText,
  MenuItem,
  SelectChangeEvent,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import countryRegionData, { Country, Region } from "country-region-data";
import { Form, Formik, FormikProps } from "formik";
import * as yup from "yup";

import { Hero2Text } from "components/shared/Typography";
import Alert from "components/shared/Alert";
import FormikFormField from "components/shared/FormikFormField";
import { ASSIGNED_USERS } from "helpers/apis";
import useAxios from "helpers/axios";
import { CUSTOMER_SUPPORT_EMAIL } from "helpers/constants";
import useAuth from "hooks/useAuth";
import { IForm, errors } from "types/form";
import { IUser, UserRole } from "types/user";

interface IAccountDetailForm extends IForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  jobTitle: string;
  companyName: string;
  address1?: string;
  address2?: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  role: UserRole;
}

const validationSchema = yup.object({
  firstName: yup.string().required(errors.required),
  lastName: yup.string().required(errors.required),
  email: yup.string().email(errors.invalidEmail).required(errors.required),
  phone: yup
    .string()
    .matches(
      /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
      errors.invalidPhone
    )
    .required(errors.required),
  jobTitle: yup.string().required(errors.required),
  companyName: yup.string().required(errors.required),
  address1: yup.string(),
  address2: yup.string(),
  city: yup.string().required(errors.required),
  state: yup.string().required(errors.required),
  country: yup.string().required(errors.required),
  postalCode: yup.string().required(errors.required),
  role: yup
    .mixed<UserRole>()
    .oneOf(Object.values(UserRole))
    .required(errors.required),
});

export const AccountDetail = ({ userId }: { userId?: number }) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const [user, setUser] = useState<IUser>();
  const [noticeMessage, setNoticeMessage] = useState<string | null>(null);
  const { user: authUser, hasPermission } = useAuth();
  const isEditable = false;
  const { fetchData, loading } = useAxios({
    config: { url: `${ASSIGNED_USERS}${userId}` },
    callOnInit: false,
    successCallback: ({ response }: any) => {
      if (response !== null) {
        setUser(response.data);
      }
    },
  });
  const isAdmin = hasPermission([UserRole.Admin]);
  const xs = useMediaQuery((theme: Theme) => theme.breakpoints.only("xs"));
  let timer: NodeJS.Timeout;

  useEffect(() => {
    if (userId) {
      fetchData();
    } else {
      setUser(authUser as IUser);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [userId]);

  useEffect(() => {
    if (noticeMessage) {
      clearTimeout(timer);
      timer = setTimeout(() => setNoticeMessage(null), 30000);
    }
  }, [noticeMessage]);

  const onSubmit = (data: IAccountDetailForm) => {
    fetchData(
      { url: `${ASSIGNED_USERS}${userId}/`, method: "put", data },
      ({
        response,
      }: {
        response: {
          data: IAccountDetailForm;
        };
      }) => {
        setUser(
          (user) =>
            ({
              ...user,
              ...(response.data || {}),
            } as IUser)
        );
        setNoticeMessage("User's Role was updated.");
      }
    );
  };

  return (
    <Accordion
      square
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
    >
      <AccordionSummary
        id="user-detail-panel-header"
        aria-controls="user-detail-panel-content"
      >
        <Hero2Text>{userId ? "User's" : "Your"} Personal Detail</Hero2Text>
      </AccordionSummary>
      <AccordionDetails>
        {loading && (
          <Box display="flex" width={1} justifyContent="center">
            <CircularProgress />
          </Box>
        )}
        {!loading && (
          <Formik<IAccountDetailForm>
            initialValues={{
              firstName: user?.firstName || "",
              lastName: user?.lastName || "",
              email: user?.email || "",
              phone: user?.phone || "",
              jobTitle: user?.jobTitle || "",
              companyName: user?.companyName || "",
              address1: user?.address1 || "",
              address2: user?.address2 || "",
              city: user?.city || "",
              state: user?.state || "",
              country: user?.country || "",
              postalCode: user?.postalCode || "",
              role: user?.role || UserRole.Customer,
            }}
            enableReinitialize
            onSubmit={onSubmit}
            validationSchema={validationSchema}
          >
            {(props: FormikProps<IAccountDetailForm>) => {
              const {
                dirty,
                isSubmitting,
                isValid,
                values,
                resetForm,
                setFieldValue,
              } = props;
              const customProps = {
                ...props,
                textFieldProps: {
                  InputProps: {
                    readOnly: !isEditable,
                  },
                },
              };

              return (
                <Form>
                  {!userId && (
                    <Alert severity="info" noDismiss>
                      Did you need help?{" "}
                      <Link
                        color="primary"
                        href={`mailto:${CUSTOMER_SUPPORT_EMAIL}?subject=User Account Support`}
                        target="_blank"
                        underline="always"
                        sx={{ textDecorationColor: "unset" }}
                      >
                        Email Customer Service.
                      </Link>
                    </Alert>
                  )}

                  {noticeMessage && <Alert noDismiss>{noticeMessage}</Alert>}
                  {userId && (
                    <Box mt={3}>
                      <Typography variant="h5">User's Role</Typography>
                      <Box mt={2}>
                        <Grid container>
                          <Grid item md={6} xs={12}>
                            <FormikFormField<IAccountDetailForm>
                              name="role"
                              {...customProps}
                              textFieldProps={{
                                select: true,
                                SelectProps: {
                                  disabled: !isAdmin,
                                  value: values.role,
                                  renderValue: (role) => role as UserRole,
                                  onChange: (
                                    event: SelectChangeEvent<unknown>
                                  ) => {
                                    setFieldValue(
                                      "role",
                                      event.target.value as UserRole
                                    );
                                  },
                                },
                                children: Object.values(UserRole).map(
                                  (role: UserRole) => (
                                    <MenuItem key={role} value={role}>
                                      <Checkbox
                                        checked={
                                          values.role!.indexOf(role) > -1
                                        }
                                      />
                                      <ListItemText primary={role} />
                                    </MenuItem>
                                  )
                                ),
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                  )}
                  <Box mt={3} mb={2}>
                    <Typography variant="subtitle1" component="h5">
                      {userId ? "User's" : "Your"} Information
                    </Typography>
                  </Box>
                  <Box mb={3}>
                    <Grid container spacing={3}>
                      <Grid item md={6} xs={12}>
                        <FormikFormField<IAccountDetailForm>
                          name="firstName"
                          {...customProps}
                        />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <FormikFormField name="lastName" {...customProps} />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <FormikFormField
                          name="email"
                          label="Email Address"
                          type="email"
                          {...customProps}
                        />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <FormikFormField
                          name="phone"
                          type="tel"
                          label="Phone Number"
                          {...customProps}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Box mt={5} mb={2}>
                    <Typography variant="subtitle1" component="h5">
                      {userId ? "User's" : "Your"} Personal Company/Institution
                    </Typography>
                  </Box>
                  <Box mb={3}>
                    <Grid container spacing={3}>
                      <Grid item md={6} xs={12}>
                        <FormikFormField name="jobTitle" {...customProps} />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <FormikFormField name="companyName" {...customProps} />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <FormikFormField name="address1" {...customProps} />
                      </Grid>
                      <Grid item md={6} xs={12}>
                        <FormikFormField name="address2" {...customProps} />
                      </Grid>
                      <Grid item md={3} xs={12}>
                        {isEditable ? (
                          <FormikFormField
                            name="country"
                            {...props}
                            textFieldProps={{
                              select: true,
                              children: countryRegionData.map(
                                (country: Country) => (
                                  <MenuItem
                                    key={country.countryShortCode}
                                    value={country.countryShortCode}
                                  >
                                    {country.countryName}
                                  </MenuItem>
                                )
                              ),
                            }}
                          />
                        ) : (
                          <FormikFormField name="country" {...customProps} />
                        )}
                      </Grid>
                      <Grid item md={3} xs={12}>
                        {isEditable ? (
                          <FormikFormField
                            name="state"
                            {...props}
                            textFieldProps={{
                              select: true,
                              children: values["country"]
                                ? countryRegionData
                                    .find(
                                      (country: Country) =>
                                        country.countryShortCode ===
                                        values["country"]
                                    )
                                    ?.regions.map((region: Region) => (
                                      <MenuItem
                                        key={region.shortCode}
                                        value={region.shortCode}
                                      >
                                        {region.name}
                                      </MenuItem>
                                    ))
                                : [<MenuItem key="empty">No States</MenuItem>],
                              SelectProps: {
                                displayEmpty: true,
                              },
                            }}
                          />
                        ) : (
                          <FormikFormField name="state" {...customProps} />
                        )}
                      </Grid>
                      <Grid item md={3} xs={12}>
                        <FormikFormField name="city" {...customProps} />
                      </Grid>
                      <Grid item md={3} xs={12}>
                        <FormikFormField name="postalCode" {...customProps} />
                      </Grid>
                    </Grid>
                  </Box>

                  {isAdmin && (
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap="16px"
                    >
                      <Box order={xs ? 1 : "unset"}>
                        <Button
                          id="cancelUserDetails"
                          color="primary"
                          disabled={!dirty}
                          variant="text"
                          onClick={() => {
                            resetForm();
                            setNoticeMessage(
                              "No changes have been made to the user's personal detail."
                            );
                          }}
                          fullWidth={xs}
                        >
                          Cancel
                        </Button>
                      </Box>
                      <Button
                        id="saveUserDetails"
                        color="primary"
                        disabled={!dirty || !isValid || isSubmitting}
                        variant="contained"
                        type="submit"
                        fullWidth={xs}
                      >
                        Save Changes
                      </Button>
                    </Box>
                  )}
                </Form>
              );
            }}
          </Formik>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
